<script setup>
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    DashboardOutlined,
    UserOutlined,
    LogoutOutlined,
    TeamOutlined,
    LoadingOutlined
} from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { resetAdminPassword } from '@/api/auth/resetPassword';
import Breadcrumb from '@/components/navigation/Breadcrumb.vue';
import { useAdminInfo } from '@/stores/useAdminInfo';
import { useActivityStore } from '@/stores/IceFire/activity';
import { canFetchIceFireActivitySetting } from '@/constants/permissionRules';
import SideMenu from '@/components/navigation/SideMenu.vue';
import TabsNav from '@/components/navigation/TabsNav.vue';

const adminStore = useAdminInfo();
const activityStore = useActivityStore();
const collapsed = ref(false);
const isMobile = ref(false);
const router = useRouter();
const route = useRoute();
const username = ref('');
const isLoading = ref(true);
const adminInfo = computed(() => adminStore.getAdminInfo());
const isIceFireRoute = computed(() => route.path.startsWith('/ice-fire'));

/** keep-alive 快取鍵：同 name 但不同 params（如 /members/:id）需分開，否則會顯示上一筆詳情 */
const viewCacheKey = computed(() => {
    const name = route.name != null ? String(route.name) : route.path;
    const params = route.params || {};
    const paramKeys = Object.keys(params).sort();
    if (paramKeys.length === 0) return name;
    const paramPart = paramKeys.map((k) => `${k}=${params[k]}`).join('&');
    return `${name}?${paramPart}`;
});

const canLoadIceFireActivity = computed(() =>
    canFetchIceFireActivitySetting(adminStore.permissions)
);

const tryLoadIceFireActivity = async () => {
    if (
        !isIceFireRoute.value ||
        activityStore.loaded ||
        !canLoadIceFireActivity.value
    ) {
        return;
    }
    await activityStore.fetch();
};

// 修改密碼相關狀態
const isPasswordModalVisible = ref(false);
const isSubmittingPassword = ref(false);
const passwordForm = ref({
    old_password: '',
    new_password: ''
});

const openPasswordModal = () => {
    isPasswordModalVisible.value = true;
};

const closePasswordModal = () => {
    isPasswordModalVisible.value = false;
    passwordForm.value = {
        old_password: '',
        new_password: ''
    };
};

const validatePasswordForm = () => {
    const { old_password, new_password } = passwordForm.value;

    if (!old_password || !new_password) {
        message.error('請輸入舊密碼與新密碼');
        return false;
    }

    if (typeof new_password !== 'string' || new_password.length < 6) {
        message.error('新密碼長度至少需要 6 碼');
        return false;
    }

    return true;
};

const submitPasswordChange = async () => {
    if (!validatePasswordForm()) return;

    try {
        isSubmittingPassword.value = true;
        await resetAdminPassword({
            old_password: passwordForm.value.old_password,
            new_password: passwordForm.value.new_password
        });

        message.success('密碼修改成功，請重新登入');
        closePasswordModal();

        await adminStore.logout();
        router.push('/login');
    } catch (error) {
        // 具體錯誤訊息已由 handleHttpError 處理，這裡只簡單提示
        message.error(error?.message || '密碼修改失敗，請稍後再試');
    } finally {
        isSubmittingPassword.value = false;
    }
};

const checkScreen = () => {
    isMobile.value = window.innerWidth <= 414;
    if (isMobile.value) {
        collapsed.value = true;
    }
};

const goToDashboard = () => {
    router.push('/dashboard');
};

const handleLogout = async () => {
    await adminStore.logout();
    router.push('/login');
};

onMounted(async () => {
    checkScreen();
    window.addEventListener('resize', checkScreen);

    // 僅還原 token／顯示名稱；權限以 router 已打過的 /me 為準
    if (!adminStore.profileFetched) {
        adminStore.loadFromStorage();
    }

    // 若 token 存在但 session 尚未拉 profile，補打一次
    if (adminStore.accessToken && !adminStore.profileFetched) {
        await adminStore.fetchUserInfo();
    }

    await tryLoadIceFireActivity();

    // 等待下一個 tick，確保 layout 完全渲染
    await nextTick();
    // 添加一個小延遲，確保過渡效果順暢
    setTimeout(() => {
        isLoading.value = false;
    }, 800);

    // 進入會員詳情、事業部管理、任務列表或活動管理時，自動收合側選單
    if (
        route.name === 'MemberDetail' ||
        route.name === 'Departments' ||
        route.name === 'MissionsList' ||
        route.name === 'MissionReviews' ||
        route.name === 'MissionRecords' ||
        route.name === 'FormActivities'
    ) {
        collapsed.value = true;
    }
});

onUnmounted(() => {
    window.removeEventListener('resize', checkScreen);
});

/** 本機 LOCAL；測試機 TEST。正式建置不帶 VITE_APP_ENV=test，也不走 staging mode。 */
const envBadge = computed(() => {
    const host =
        typeof window !== 'undefined' ? String(window.location.hostname || '') : '';
    if (host === 'localhost' || host === '127.0.0.1' || host.startsWith('127.')) {
        return 'LOCAL';
    }
    if (
        import.meta.env.VITE_APP_ENV === 'test' ||
        import.meta.env.MODE === 'staging'
    ) {
        return 'TEST';
    }
    return '';
});

const sideWidth = computed(() => {
    if (isMobile.value) return 0;
    return collapsed.value ? 80 : 200;
});

// 監聽路由切換：若進入會員詳情、事業部管理、任務列表或活動管理，自動收合
watch(
    () => route.name,
    (name) => {
        if (
            name === 'MemberDetail' ||
            name === 'Departments' ||
            name === 'MissionsList' ||
            name === 'MissionReviews' ||
            name === 'MissionRecords' ||
            name === 'FormActivities'
        ) {
            collapsed.value = true;
        }
    }
);

watch(
    () => route.path,
    async (path) => {
        if (!path.startsWith('/ice-fire')) return;
        await tryLoadIceFireActivity();
    }
);
</script>

<template>
    <a-layout class="vaporwave-layout">
        <!-- 背景：低調淺底，避免設定頁被格線／光球干擾 -->
        <div class="vaporwave-bg-decoration" aria-hidden="true">
            <div class="bg-gradient"></div>
        </div>

        <div
            v-if="isLoading"
            class="fixed inset-0 vaporwave-loading z-[999] flex items-center justify-center"
        >
            <div class="text-center">
                <div class="vaporwave-spinner">
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                </div>
                <div class="mt-8 text-aqua-cyan text-md font-mono">
                    載入中......
                </div>
            </div>
        </div>

        <!-- 手機版遮罩 -->
        <div
            v-if="isMobile && !collapsed"
            class="fixed inset-0 bg-black bg-opacity-50 z-[90] backdrop-blur-sm"
            @click="collapsed = true"
        ></div>

        <!-- 側選單 -->
        <a-layout-sider
            v-model:collapsed="collapsed"
            collapsible
            :trigger="null"
            theme="dark"
            class="vaporwave-sider flex flex-col shadow-2xl transition-all duration-300"
            :class="{
                'fixed left-0 top-0 h-screen z-[100]': isMobile,
                'fixed left-0 top-0 h-screen': !isMobile,
                'translate-x-[-100%]': isMobile && collapsed,
                'translate-x-0': isMobile && !collapsed
            }"
        >
            <div v-if="envBadge" class="env-test-tag">{{ envBadge }}</div>
            <!-- 標題 / Logo -->
            <div class="vaporwave-sider-header">
                <div
                    class="my-4 h-8 flex items-center justify-center transition-all duration-300"
                    :class="{
                        'w-10 mx-auto': collapsed,
                        'w-[180px] mx-4': !collapsed
                    }"
                >
                    <div class="min-w-0">
                        <div
                            v-if="!collapsed && !(isMobile && !collapsed)"
                            class="flex items-center gap-2 cursor-pointer min-w-0"
                            @click="goToDashboard"
                        >
                            <div class="vaporwave-logo">
                                <img
                                    src="/worldtree-core-logo.png"
                                    alt="世界樹核心 logo"
                                    class="vaporwave-logo-img"
                                />
                            </div>
                            <span
                                class="text-lg font-medium text-gray-700 vaporwave-title truncate"
                            >
                                世界樹核心
                            </span>
                        </div>
                        <div
                            v-if="collapsed && !isMobile"
                            class="vaporwave-logo-collapsed"
                        >
                            <img
                                src="/worldtree-core-logo.png"
                                alt="世界樹核心 logo"
                                class="vaporwave-logo-img"
                            />
                        </div>
                    </div>
                </div>

                <!-- 手機版選單打開時的按鈕 -->
                <div
                    v-if="isMobile && !collapsed"
                    class="mr-4 text-xl cursor-pointer text-[var(--accent)] hover:text-[var(--accent-strong)] transition-colors"
                    @click="() => (collapsed = !collapsed)"
                >
                    <menu-unfold-outlined
                        class="transition-transform duration-300 rotate-180"
                    />
                </div>
            </div>

            <!-- Header 與選單的分隔帶：不要額外畫線（避免變醜） -->

            <!-- 側選單 -->
            <SideMenu
                :key="isMobile ? 'mobile' : 'desktop'"
                :collapsed="collapsed"
                :isMobile="isMobile"
            />
        </a-layout-sider>

        <!-- 主要內容區域 -->
        <a-layout
            class="vaporwave-main transition-all duration-300 min-h-screen"
            :class="{
                'ml-0 w-full': isMobile,
                'ml-[200px] w-[calc(100%-200px)]': !isMobile && !collapsed,
                'ml-[80px] w-[calc(100%-80px)]': !isMobile && collapsed
            }"
        >
            <!-- Header -->
            <a-layout-header
                class="vaporwave-header !px-0 flex justify-between items-center fixed z-50"
                :class="{
                    'w-full left-0': isMobile,
                    'w-[calc(100%-200px)] left-[200px]':
                        !isMobile && !collapsed,
                    'w-[calc(100%-80px)] left-[80px]': !isMobile && collapsed
                }"
            >
                <div class="flex items-center">
                    <div
                        v-if="!isMobile || (isMobile && collapsed)"
                        class="ml-[12px] w-[20px] text-xl leading-[64px] cursor-pointer transition-colors text-gray-600 hover:text-[var(--accent)]"
                        @click="() => (collapsed = !collapsed)"
                    >
                        <menu-unfold-outlined
                            class="transition-transform duration-300"
                            :class="{
                                'rotate-0': collapsed,
                                'rotate-180': !collapsed
                            }"
                        />
                    </div>
                </div>

                <!-- 手機版中間 Logo -->
                <div
                    v-if="isMobile"
                    class="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2"
                >
                    <div class="vaporwave-logo-mobile">
                        <img
                            src="/worldtree-core-logo.png"
                            alt="世界樹核心 logo"
                            class="vaporwave-logo-img mobile"
                        />
                    </div>
                    <span
                        class="text-lg font-medium text-gray-700 vaporwave-title"
                    >
                        世界樹核心
                    </span>
                </div>

                <div class="flex items-center">
                    <a-dropdown
                        class="mr-6"
                        :trigger="isMobile ? ['click'] : ['hover']"
                    >
                        <div
                            class="vaporwave-user-menu flex items-center gap-2 px-4 cursor-pointer"
                        >
                            <span
                                v-if="!isMobile"
                                class="text-gray-600 mr-2 text-sm"
                            >
                                {{ adminInfo.role }}
                            </span>
                            <span
                                v-if="!isMobile"
                                class="text-gray-700 mr-2 text-sm max-w-[120px] truncate"
                            >
                                {{ adminInfo.name || 'Loading...' }}
                            </span>
                            <user-outlined
                                class="text-gray-600"
                                :class="isMobile ? 'mr-[-24px]' : ''"
                            />
                        </div>
                        <template #overlay>
                            <a-menu class="vaporwave-dropdown-menu">
                                <a-menu-item v-if="isMobile" disabled>
                                    <div class="flex items-center gap-2">
                                        <span class="text-gray-700">
                                            {{ adminInfo.name }}
                                        </span>
                                    </div>
                                </a-menu-item>
                                <a-menu-divider v-if="isMobile" />
                                <a-menu-item @click="openPasswordModal">
                                    <div class="flex items-center gap-2">
                                        <span>修改密碼</span>
                                    </div>
                                </a-menu-item>
                                <a-menu-divider />
                                <a-menu-item @click="handleLogout">
                                    <div
                                        class="logout-menu-item-content flex items-center gap-2"
                                    >
                                        <logout-outlined />
                                        <span>登出</span>
                                    </div>
                                </a-menu-item>
                            </a-menu>
                        </template>
                    </a-dropdown>
                </div>
            </a-layout-header>

            <!-- Views 區域 -->
            <div class="pt-16 min-h-screen w-full">
                <!-- 麵包屑和標題 -->
                <div
                    class="vaporwave-breadcrumb fixed z-40"
                    :class="{
                        'w-full left-0': isMobile,
                        'w-[calc(100%-200px)] left-[200px]':
                            !isMobile && !collapsed,
                        'w-[calc(100%-80px)] left-[80px]':
                            !isMobile && collapsed
                    }"
                >
                    <div :class="isMobile ? 'px-2' : 'px-6'" class="pt-4 pb-2">
                        <Breadcrumb />
                        <TabsNav />
                    </div>
                </div>

                <!-- 內容區域 -->
                <div
                    :class="[
                        isMobile ? 'mx-0 px-0' : 'px-6',
                        'admin-app-content',
                        { 'ice-fire-route': isIceFireRoute }
                    ]"
                    class="mt-36 pb-20"
                >
                    <a-alert
                        v-if="
                            isIceFireRoute &&
                            activityStore.loaded &&
                            !activityStore.enabled
                        "
                        class="ice-fire-activity-banner mb-4"
                        type="warning"
                        show-icon
                        message="冰火副本目前未啟用，前台不顯示；後台仍可運作"
                    />
                    <router-view v-slot="{ Component }">
                        <!-- 多頁籤切換時保留頁面狀態（篩選、捲動等）；上限避免記憶體無限成長 -->
                        <!-- key 含 route params，避免會員詳情等同名動態路由共用快取 -->
                        <keep-alive :max="25">
                            <component :is="Component" :key="viewCacheKey" />
                        </keep-alive>
                    </router-view>
                </div>
            </div>

            <!-- 修改密碼彈窗 -->
            <a-modal
                v-model:open="isPasswordModalVisible"
                title="修改密碼"
                :footer="null"
                centered
                :maskClosable="false"
                class="vaporwave-password-modal"
                wrapClassName="vaporwave-password-modal-wrap"
            >
                <div class="flex flex-col gap-4">
                    <a-form layout="vertical">
                        <a-form-item label="舊密碼" required>
                            <a-input-password
                                v-model:value="passwordForm.old_password"
                                autocomplete="current-password"
                                class="w-full vaporwave-password-input"
                            />
                        </a-form-item>
                        <a-form-item
                            label="新密碼"
                            required
                            extra="長度至少 6 碼"
                        >
                            <a-input-password
                                v-model:value="passwordForm.new_password"
                                autocomplete="new-password"
                                class="w-full vaporwave-password-input"
                            />
                        </a-form-item>
                    </a-form>

                    <div class="flex justify-end gap-2 mt-2">
                        <a-button
                            class="px-4 vaporwave-btn-secondary"
                            :disabled="isSubmittingPassword"
                            @click="closePasswordModal"
                        >
                            取消
                        </a-button>
                        <a-button
                            type="primary"
                            class="px-4 vaporwave-btn-primary"
                            :loading="isSubmittingPassword"
                            @click="submitPasswordChange"
                        >
                            確認修改並重新登入
                        </a-button>
                    </div>
                </div>
            </a-modal>
        </a-layout>
    </a-layout>
</template>

<style scoped>
/* 蒸氣波風格 Layout */
.vaporwave-layout {
    height: 100vh;
    background: var(--bg);
    font-family: var(--font-ui);
    position: relative;
    overflow: hidden;
}

/* 背景裝飾 */
.vaporwave-bg-decoration {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
}

.bg-gradient {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #f4f5f7;
}

/* 載入動畫 */
.vaporwave-loading {
    background: #d0d0d5;
    backdrop-filter: blur(10px);
}

.vaporwave-spinner {
    position: relative;
    width: 80px;
    height: 80px;
}

.spinner-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 3px solid transparent;
    border-top: 3px solid var(--aqua-cyan);
    border-radius: 50%;
    animation: spin 1.5s linear infinite;
}

.spinner-ring:nth-child(2) {
    width: 70%;
    height: 70%;
    top: 15%;
    left: 15%;
    border-top-color: var(--sunset-pink);
    animation-delay: 0.5s;
}

.spinner-ring:nth-child(3) {
    width: 40%;
    height: 40%;
    top: 30%;
    left: 30%;
    border-top-color: var(--neon-purple);
    animation-delay: 1s;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* 側邊欄 */
.vaporwave-sider {
    background: #edf2f7 !important;
    backdrop-filter: none;
    border-right: 1px solid rgba(21, 37, 54, 0.1);
    box-shadow: none;
    position: fixed !important;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 100;
}

.env-test-tag {
    margin: 10px 12px 0;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.16em;
    color: #7a3412;
    background: #ffe8d6;
    border: 1px solid #e8a06a;
    border-radius: 999px;
}

.vaporwave-sider.ant-layout-sider-collapsed .env-test-tag {
    margin: 8px 8px 0;
    letter-spacing: 0.04em;
    font-size: 10px;
}

.vaporwave-sider-header {
    background: var(--paper);
    backdrop-filter: none;
    border-bottom: 1px solid var(--line);
    box-shadow: none;
    position: relative;
    height: 64px;
    display: flex;
    align-items: center;
    padding: 0;
}

.vaporwave-sider-header::after,
.vaporwave-sider-header::before {
    content: none !important;
    display: none !important;
}

.vaporwave-logo {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(56, 120, 200, 0.35);
}

.vaporwave-logo-collapsed {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0 4px 12px rgba(56, 120, 200, 0.35);
}

.vaporwave-logo-mobile {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
}

.logo-icon {
    font-size: 20px;
    color: #dbe4f0;
    position: relative;
    z-index: 1;
    opacity: 0.95;
}

.vaporwave-logo-mobile .logo-icon {
    font-size: 16px;
}

.vaporwave-logo-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.vaporwave-logo-img.mobile {
    width: 100%;
    height: 100%;
}

/* 收合狀態時縮小 Logo，避免太搶眼 */
.vaporwave-sider.ant-layout-sider-collapsed .vaporwave-logo-collapsed,
.vaporwave-sider.ant-layout-sider-collapsed .vaporwave-logo {
    width: 32px;
    height: 32px;
    box-shadow: 0 4px 10px rgba(56, 120, 200, 0.3);
}

/* ✅ 移除額外分隔帶（避免多一條線變醜） */

.logo-glow {
    display: none;
}

.vaporwave-title {
    text-shadow: none;
    font-weight: 700;
    font-family: var(--font-ui);
    letter-spacing: 0.02em;
    color: inherit;
}

.vaporwave-sider .vaporwave-title,
.vaporwave-header .vaporwave-title {
    color: #152536 !important;
}

/* 主內容區域 */
.vaporwave-main {
    background: var(--bg);
    height: 100vh;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    position: relative;
}

.vaporwave-main::-webkit-scrollbar {
    display: none;
}

/* Header */
.vaporwave-header {
    background: var(--paper) !important;
    backdrop-filter: none;
    border-bottom: 1px solid var(--line);
    box-shadow: none;
}

.vaporwave-user-menu {
    color: var(--ink-soft);
    transition: all 0.2s ease;
    border-radius: var(--radius-sm);
    padding: 8px 12px;
}

.vaporwave-user-menu .text-aqua-cyan {
    color: var(--accent) !important;
    text-shadow: none;
    transition: all 0.2s ease;
}

.vaporwave-user-menu .text-sunset-pink {
    color: var(--muted) !important;
    text-shadow: none;
    transition: all 0.2s ease;
}

.vaporwave-user-menu:hover {
    color: var(--accent);
    background: var(--accent-soft);
}

.vaporwave-user-menu:hover .text-aqua-cyan {
    color: var(--accent-strong) !important;
    text-shadow: none;
}

.vaporwave-user-menu:hover .text-sunset-pink {
    color: var(--ink-soft) !important;
    text-shadow: none;
}

/* 下拉選單 */
.vaporwave-dropdown-menu {
    background: var(--paper) !important;
    backdrop-filter: none;
    border: 1px solid var(--line);
    border-radius: var(--radius);
    box-shadow: var(--shadow-card);
}

/* 目標鎖定登出按鈕的內容 */
.vaporwave-dropdown-menu :deep(.logout-menu-item-content *) {
    color: #4a4a5a !important;
    transition: color 0.3s ease !important;
}

.vaporwave-dropdown-menu
    :deep(.ant-menu-item:hover .logout-menu-item-content *) {
    color: #2a2a3a !important;
}

.vaporwave-dropdown-menu .ant-menu-item {
    /* 移除針對 item 的顏色設定，避免衝突 */
    transition: all 0.3s ease;
}

.vaporwave-dropdown-menu .ant-menu-item:hover {
    background: rgba(42, 74, 122, 0.08) !important;
    /* 移除針對 item 的顏色設定，避免衝突 */
}

.vaporwave-dropdown-menu .ant-menu-item-selected {
    background: rgba(42, 74, 122, 0.14) !important;
    color: #4a4a5a !important;
}

/* 修改密碼彈窗（對齊後台蒸氣波風格）
 * 注意：Modal 會 teleport 到 body，scoped 下需用 :deep 包住整段選擇器
 * 並且用 wrapClassName 鎖定，避免影響其他頁面
 */
:deep(.vaporwave-password-modal-wrap .ant-modal-content) {
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px);
    border: 2px solid var(--neon-purple);
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(42, 74, 122, 0.16);
    overflow: hidden;
}

:deep(.vaporwave-password-modal-wrap .ant-modal-header) {
    background: transparent !important;
    border-bottom: 1px solid rgba(42, 74, 122, 0.16) !important;
    padding: 18px 24px;
}

:deep(.vaporwave-password-modal-wrap .ant-modal-title) {
    font-family: var(--font-ui);
    font-weight: 700;
    color: #2a4a7a;
    text-shadow: 0 1px 3px rgba(42, 74, 122, 0.16);
}

:deep(.vaporwave-password-modal-wrap .ant-modal-close) {
    color: #2a4a7a;
}

:deep(.vaporwave-password-modal-wrap .ant-modal-close:hover) {
    color: var(--sunset-pink);
}

:deep(.vaporwave-password-modal-wrap .ant-modal-body) {
    padding: 20px 24px 24px;
}

:deep(.vaporwave-password-modal-wrap .ant-form-item-label > label) {
    font-family: var(--font-ui);
    color: #4a4a5a;
}

:deep(.vaporwave-password-modal-wrap .ant-form-item-explain),
:deep(.vaporwave-password-modal-wrap .ant-form-item-extra) {
    color: rgba(90, 74, 122, 0.75);
}

:deep(
    .vaporwave-password-modal-wrap
        .ant-input-affix-wrapper.vaporwave-password-input
),
:deep(
    .vaporwave-password-modal-wrap
        .vaporwave-password-input.ant-input-affix-wrapper
),
:deep(
    .vaporwave-password-modal-wrap
        .vaporwave-password-input
        .ant-input-affix-wrapper
) {
    border-radius: 10px;
    border: 1px solid rgba(42, 74, 122, 0.35) !important;
    background: rgba(255, 255, 255, 0.9) !important;
    transition: all 0.25s ease;
}

:deep(
    .vaporwave-password-modal-wrap
        .ant-input-affix-wrapper.vaporwave-password-input:hover
),
:deep(
    .vaporwave-password-modal-wrap
        .vaporwave-password-input.ant-input-affix-wrapper:hover
),
:deep(
    .vaporwave-password-modal-wrap
        .vaporwave-password-input
        .ant-input-affix-wrapper:hover
) {
    border-color: rgba(168, 255, 246, 0.7) !important;
    box-shadow: 0 0 0 3px rgba(168, 255, 246, 0.15);
}

:deep(
    .vaporwave-password-modal-wrap
        .ant-input-affix-wrapper-focused.vaporwave-password-input
),
:deep(
    .vaporwave-password-modal-wrap
        .vaporwave-password-input.ant-input-affix-wrapper-focused
),
:deep(
    .vaporwave-password-modal-wrap
        .vaporwave-password-input
        .ant-input-affix-wrapper-focused
) {
    border-color: rgba(168, 255, 246, 0.9) !important;
    box-shadow: 0 0 0 3px rgba(168, 255, 246, 0.22);
}

:deep(
    .vaporwave-password-modal-wrap
        button.ant-btn.ant-btn-primary.vaporwave-btn-primary
),
:deep(
    .vaporwave-password-modal-wrap
        button.ant-btn.ant-btn-primary.vaporwave-btn-primary:hover
),
:deep(
    .vaporwave-password-modal-wrap
        button.ant-btn.ant-btn-primary.vaporwave-btn-primary:active
),
:deep(
    .vaporwave-password-modal-wrap
        button.ant-btn.ant-btn-primary.vaporwave-btn-primary:focus
),
:deep(
    .vaporwave-password-modal-wrap
        button.ant-btn.ant-btn-primary.vaporwave-btn-primary:focus-visible
) {
    border-color: transparent !important;
    background: transparent !important;
    background-image: var(--primary-btn-gradient) !important;
    box-shadow: 0 8px 18px rgba(138, 175, 255, 0.22) !important;
    transition: all 0.25s ease;
}

:deep(
    .vaporwave-password-modal-wrap
        button.ant-btn.ant-btn-primary.vaporwave-btn-primary:hover
) {
    filter: brightness(1.06);
    box-shadow:
        0 10px 22px rgba(42, 74, 122, 0.22),
        0 0 18px rgba(168, 255, 246, 0.22) !important;
}

:deep(
    .vaporwave-password-modal-wrap
        button.ant-btn.ant-btn-primary.vaporwave-btn-primary
        > span
) {
    color: rgba(255, 255, 255, 0.95) !important;
}

:deep(
    .vaporwave-password-modal-wrap
        button.ant-btn.ant-btn-primary.vaporwave-btn-primary[disabled]
) {
    opacity: 0.6;
    box-shadow: none !important;
}

:deep(.vaporwave-password-modal-wrap .vaporwave-btn-secondary.ant-btn) {
    border-radius: 10px;
    border: 1px solid rgba(42, 74, 122, 0.35) !important;
    background: rgba(255, 255, 255, 0.85) !important;
    color: #2a4a7a !important;
    transition: all 0.25s ease;
}

:deep(.vaporwave-password-modal-wrap .vaporwave-btn-secondary.ant-btn:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    background: rgba(42, 74, 122, 0.08) !important;
}

/* 麵包屑區域 */
.vaporwave-breadcrumb {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(42, 74, 122, 0.14);
    box-shadow: 0 2px 10px rgba(42, 74, 122, 0.08);
}

.ice-fire-activity-banner {
    border-color: rgba(212, 167, 44, 0.45);
    background: rgba(254, 240, 224, 0.92);
    color: #5a4a1f;
    font-family: var(--font-ui);
}

/* 響應式設計 */
@media (max-width: 768px) {
    .vaporwave-title {
        font-size: 1rem;
    }

    .vaporwave-sider {
        border-right-width: 1px;
    }

    .vaporwave-header {
        border-bottom-width: 1px;
    }
}
</style>
